/**
 * GlobalTableSessionBar — 활성 테이블 세션 글로벌 스테이터스 레이어 (#660).
 *
 * 테이블 세션이 활성인 동안 앱 전역(어느 라우트든)에서 하단 네비게이션 위에 고정 노출되어
 * "지금 테이블 세션이 진행 중"임을 보여주고, 탭하면 해당 테이블 오더 페이지
 * (`/restaurant/{storeId}/table/{tableNumber}`)로 복귀한다. 뒤로가기 등으로 테이블 화면을
 * 이탈하면 복귀 수단이 없던 문제(#660 — #657 결제 결과 화면 복귀의 전역 일반화)를 시정한다.
 *
 * ## 노출 판정 (fail-safe — 하나라도 불만족 시 미렌더)
 *   - `getTableSessionReturnContext()` (#657 / PR #658 SSOT, #670 record 폴백) —
 *     localStorage 복구 record(미만료) 기준 + sessionStorage 3키 존재 시 token 일치 강제.
 *     픽업 매장 진입 등 비종료성 이동이 sessionStorage 를 비워도 record 단독으로 유지
 *     (#670 — 세션 종료/만료/명시 이탈은 record 까지 제거되므로 자동 미렌더).
 *   - 테이블 오더 페이지(`/table/{n}` path)에서는 중복 억제 — 그 화면은 이미
 *     SessionControlBar(타이머) + SessionProgressBar(진행 현황)가 담당한다.
 *   - checkout / payment 라우트는 App 레벨에서 BottomNavigation 과 함께 미렌더
 *     (결제 흐름 방해 금지 — 결제 결과 복귀는 #657 returnToStore 버튼이 담당).
 *
 * ## 만료/종료 자동 제거 (재평가 트리거 3종)
 *   - 라우트 변경 (location.pathname)
 *   - `TABLE_SESSION_CHANGED_EVENT` — clearTableSession() 등 토큰 set/clear 즉시 반응
 *   - **만료 정확 타이머** (codex I1) — expiresAt + 버퍼 시점 setTimeout 으로 즉시 제거
 *   - 보조 주기 tick (REEVALUATE_INTERVAL_MS) — 시계 드리프트/절전 복귀 안전망
 *
 * ## 세션 진행상황 (가벼운 요약 — 깊은 결합 금지)
 *   `useSessionUnpaidPostpay` 공유 훅(#603/#654 단일 출처)을 그대로 소비하되, data 가
 *   null(미결제 없음/조회 실패/비회원 자격 부재)이면 요약 줄만 생략하고 레이어는 유지한다
 *   — 진행 요약은 부가 정보이고 레이어의 본질은 "테이블 복귀 수단"이기 때문.
 *
 * ## z-계층 / 위치 (#648 #634 정책 정합)
 *   - `Z_INDEX.BOTTOM_NAVIGATION`(50) — 하단 네비와 동급. DOM 상 AppModals(z-50+, 후순위)
 *     아래라 모달이 열리면 모달이 위를 덮는다(역전 금지 정책 보존).
 *   - 하단 네비(`[data-bottom-nav]`) 실측 높이만큼 bottom offset — TabNav/PickupCartNav
 *     어느 변형이 렌더돼도 겹치지 않는다. 네비 부재 시 bottom 0 + safe-area 인셋.
 *
 * @see src/lib/tableContext.ts getTableSessionReturnContext (판정 SSOT)
 * @see src/hooks/useSessionUnpaidPostpay.ts (진행 요약 출처)
 * @see src/i18n/globalSessionBar.ts (15 locale 문구)
 */
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, UtensilsCrossed } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useOrderHistory } from '@/contexts/OrderHistoryContext';
import { useSessionUnpaidPostpay } from '@/hooks/useSessionUnpaidPostpay';
import { useSafeArea } from '@/hooks/useSafeArea';
import {
  TABLE_SESSION_CHANGED_EVENT,
  extractTableNumberFromPath,
  getTableSessionReturnContext,
} from '@/lib/tableContext';
import { getRestaurantTablePath } from '@/config/routes';
import { Z_INDEX } from '@/constants/zIndex';
import { formatCurrency } from '@/utils/currency';
import { globalSessionBarTranslations } from '@/i18n/globalSessionBar';

/**
 * 보조 재평가 주기 (ms) — 시계 드리프트/절전 복귀(타이머 지연·미발화) 대비 안전망.
 * 만료의 1차 제거는 expiresAt 기준 정확 setTimeout (codex I1) 이 담당한다.
 * storage 읽기 + 필드 비교만 수행하므로 부담 없음 (네트워크 호출 없음).
 */
export const SESSION_BAR_REEVALUATE_INTERVAL_MS = 30_000;

/**
 * 만료 정확 타이머 버퍼 (ms) — expiresAt 경과 직후 판정이 확실히 "만료"가 되도록
 * 소량 지연 후 재평가한다 (경계 시각 동시성/타이머 조기 발화 방어).
 */
export const SESSION_BAR_EXPIRY_BUFFER_MS = 250;

/** setTimeout 32bit 한계 (초과 시 즉시 발화하는 브라우저 동작 방어 — 만료가 먼 경우 클램프) */
const MAX_TIMEOUT_MS = 2_147_483_647;

/** 레이어 표시에 필요한 세션 스냅샷 (storage 재평가 결과) */
interface SessionBarSnapshot {
  storeId: number;
  tableNumber: string;
  storeName: string;
  /** record 만료 시각 (ISO) — 정확 만료 타이머 입력 (codex I1) */
  expiresAt: string;
}

/**
 * storage 기반 스냅샷 재평가 — 복귀 컨텍스트(SSOT) 단일 출처 (#670).
 * 매장명도 record 출처(context.storeName)를 사용한다 — sessionStorage 3키는
 * 픽업 매장 진입(PickupMenu clear) 등 비종료성 이동으로 비워질 수 있어, 그걸
 * 요구하면 활성 세션인데도 레이어가 사라진다 (#670 root cause — 복귀 수단 상실).
 * 불일치 거부/만료 판정은 getTableSessionReturnContext 가 담당 (판정·표시 출처 일원화).
 */
function readSessionBarSnapshot(): SessionBarSnapshot | null {
  const context = getTableSessionReturnContext();
  if (!context) return null;

  return {
    storeId: context.storeId,
    tableNumber: context.tableNumber,
    storeName: context.storeName,
    expiresAt: context.expiresAt,
  };
}

/** 스냅샷 동일성 — 동일하면 이전 참조를 유지해 주기 tick 의 불필요 리렌더를 회피 */
function isSameSnapshot(a: SessionBarSnapshot | null, b: SessionBarSnapshot | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.storeId === b.storeId &&
    a.tableNumber === b.tableNumber &&
    a.storeName === b.storeName &&
    a.expiresAt === b.expiresAt
  );
}

export function GlobalTableSessionBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, currency } = useApp();
  const { isLoggedIn } = useAuth();
  const { guestCredentials } = useOrderHistory();
  const { bottomCSS: safeAreaBottom } = useSafeArea();

  const [snapshot, setSnapshot] = useState<SessionBarSnapshot | null>(readSessionBarSnapshot);

  const reevaluate = useCallback(() => {
    const next = readSessionBarSnapshot();
    setSnapshot((prev) => (isSameSnapshot(prev, next) ? prev : next));
  }, []);

  // 재평가 — 라우트 변경 + 세션 변경 이벤트 + 보조 주기 tick (만료/종료 자동 제거).
  useEffect(() => {
    reevaluate();
    window.addEventListener(TABLE_SESSION_CHANGED_EVENT, reevaluate);
    const intervalId = window.setInterval(reevaluate, SESSION_BAR_REEVALUATE_INTERVAL_MS);
    return () => {
      window.removeEventListener(TABLE_SESSION_CHANGED_EVENT, reevaluate);
      window.clearInterval(intervalId);
    };
  }, [location.pathname, reevaluate]);

  // codex I1 — 만료 시각 기준 정확 제거: 30s 폴링만으로는 만료 후 최대 30초 잔존·클릭 가능.
  // expiresAt + 소량 버퍼 시점에 setTimeout 으로 즉시 재평가(→ 만료 판정 → 제거).
  // snapshot(expiresAt 포함) 변경 시 타이머 재설정 + 정리. 30s tick 은 보조로 유지
  // (시계 드리프트/절전 복귀 등 타이머 미발화·지연 대비).
  useEffect(() => {
    if (!snapshot) return;

    const expiresAtMs = Date.parse(snapshot.expiresAt);
    if (!Number.isFinite(expiresAtMs)) return;

    const delay = Math.min(
      Math.max(expiresAtMs - Date.now() + SESSION_BAR_EXPIRY_BUFFER_MS, 0),
      MAX_TIMEOUT_MS,
    );
    const timeoutId = window.setTimeout(reevaluate, delay);
    return () => window.clearTimeout(timeoutId);
  }, [snapshot, reevaluate]);

  // 테이블 오더 페이지에서는 중복 억제 — SessionProgressBar/SessionControlBar 존재.
  // codex P2 검증: extractTableNumberFromPath 는 `/table/{n}` 서브스트링 매칭이지만,
  // 현 라우트 표(src/config/routes.ts ROUTES)에서 `/table/` 세그먼트를 포함하는 경로는
  // RESTAURANT_TABLE(`/restaurant/:id/table/:tableNum`) 단 하나 — 과잉 억제 충돌 경로 없음.
  // (정규식이 `/table/` 양쪽 슬래시를 요구하므로 `/timetable/` 류 부분 문자열도 미매칭.)
  // 새 라우트가 `/table/` 세그먼트를 추가하면 정확 라우트 역매칭으로 좁혀야 한다.
  const onTablePage = extractTableNumberFromPath(location.pathname) !== undefined;
  const visible = snapshot !== null && !onTablePage;

  // 진행 요약 (가벼운 결합) — 실패/부재 시 data null → 요약 줄만 생략 (레이어는 유지).
  const guestPassword = isLoggedIn ? undefined : guestCredentials?.password;
  const { data: unpaid } = useSessionUnpaidPostpay({
    enabled: visible,
    isLoggedIn,
    guestPassword,
  });

  // 하단 네비 실측 높이만큼 offset — TabNav / PickupCartNav 어느 변형이든 겹침 방지.
  // codex I2 — 단발 측정 staleness 시정: (지원 시) ResizeObserver 로 네비 높이 변화를 추적하고
  // orientationchange / visualViewport resize 에도 재측정. 네비 요소 자체의 마운트/언마운트는
  // 라우트 변경 재실행(location.pathname dep)이 재탐색을 담당 (MutationObserver 는 과잉 — codex 합의).
  const [navOffset, setNavOffset] = useState(0);
  useLayoutEffect(() => {
    if (!visible) return;

    const measure = (): HTMLElement | null => {
      const nav = document.querySelector<HTMLElement>('[data-bottom-nav]');
      setNavOffset(nav ? nav.offsetHeight : 0);
      return nav;
    };

    const nav = measure();

    let resizeObserver: ResizeObserver | undefined;
    if (nav && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => measure());
      resizeObserver.observe(nav);
    }

    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', measure);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      visualViewport?.removeEventListener('resize', measure);
    };
  }, [visible, location.pathname]);

  const handleReturn = useCallback(() => {
    if (!snapshot) return;
    navigate(getRestaurantTablePath(String(snapshot.storeId), snapshot.tableNumber));
  }, [navigate, snapshot]);

  if (!visible || !snapshot) return null;

  const t = globalSessionBarTranslations[locale] ?? globalSessionBarTranslations.es;
  const tableLabel = t.tableLabel.replace('{table}', snapshot.tableNumber);

  // 미결제 요약 — 주문 수(서버 권위 count) + 합계 (있을 때만, fail-safe 생략).
  let unpaidSummary: string | null = null;
  if (unpaid && unpaid.count > 0) {
    const numericTotal = Number(unpaid.totalAmount);
    const totalDisplay = formatCurrency(Number.isFinite(numericTotal) ? numericTotal : 0, currency);
    unpaidSummary = `${t.unpaidSummary.replace('{count}', String(unpaid.count))} · ${totalDisplay}`;
  }

  return (
    <div
      data-testid="global-session-bar"
      className="fixed left-0 right-0"
      // 네비 동급 z(50) — DOM 후순위 모달(AppModals)이 위를 덮는다 (#634 역전 금지 정합).
      // 네비 부재(navOffset=0) 시 safe-area 인셋을 레이어가 직접 담당 (#648 정합).
      style={{
        bottom: navOffset,
        zIndex: Z_INDEX.BOTTOM_NAVIGATION,
        paddingBottom: navOffset === 0 ? safeAreaBottom : undefined,
      }}
    >
      <button
        type="button"
        data-testid="global-session-bar-return"
        aria-label={t.returnLabel}
        onClick={handleReturn}
        className="w-full flex items-center justify-between gap-3 px-[min(4vw,1rem)] py-2.5 bg-primary text-white text-left shadow-[0_-2px_8px_rgba(0,0,0,0.15)] hover:bg-primary-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        <div className="flex items-center gap-2 min-w-0">
          <UtensilsCrossed className="h-4 w-4 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">
              {snapshot.storeName} · {tableLabel}
            </p>
            {unpaidSummary && (
              <p data-testid="global-session-bar-summary" className="text-xs text-white/85 truncate">
                {unpaidSummary}
              </p>
            )}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 shrink-0" aria-hidden="true" />
      </button>
    </div>
  );
}
